import React, { useState, useEffect } from 'react';
import { ContentImageField } from '@/components/admin/content-image-field';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import {
  AUDIO_TYPE,
  Content,
  ContentConfig,
  ContentConfigInsert,
  ContentConfigEditOrInsert,
  ContentType,
} from '@/types/custom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import { FileText, Upload, Trash2 } from 'lucide-react';
import { ContentKeywordManager } from '@/components/admin/content-keyword-manager';
import { useContentWithConfig } from '@/lib/hooks/use-content';
import {
  Root as TabsRoot,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@radix-ui/react-tabs';
import ConfigEditView, { nameConfig } from './config-edit';
import { CONTENT_TYPES } from '@/lib/constants';

const EMPTY_CONFIG = {
  audio_link: '',
  music: true,
  voice: false,
  voice_name: null,
  transcription_url: null,
};

const isCompleteConfig = (config: ContentConfigEditOrInsert) => {
  if (!config.audio_link) return false;
  return true;
};

interface MeditationEditProps {
  content?: Content;
  onSave: () => void;
  isOpen: boolean;
  handleClose: () => void;
  defaultMeditationType?: ContentType;
}

/**
 * Dialog for editing a given meditation in place.
 */
export default function MeditationEdit({
  content: prevContent,
  onSave,
  isOpen,
  handleClose,
  defaultMeditationType,
}: MeditationEditProps) {
  const [title, setTitle] = useState(prevContent?.title || '');
  const [description, setDescription] = useState(
    prevContent?.description || '',
  );
  const [imageUrl, setImageUrl] = useState(prevContent?.image_url || '');
  const [meditationType, setMeditationType] = useState<string>(
    prevContent?.content_type || defaultMeditationType || '',
  );
  const [published, setPublished] = useState<boolean>(
    prevContent?.published || false,
  );

  // fetch existing configs (if any)
  const { contentWithConfig, loading: loadingConfigs } = useContentWithConfig(
    prevContent?.id || '',
  );
  const [prevConfigs, setPrevConfigs] = useState<ContentConfig[]>([]);
  const [newConfigs, setNewConfigs] = useState<ContentConfigEditOrInsert[]>([]);
  const [viewingConfigIdx, setViewingConfigIdx] = useState(-1);

  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    setTitle(prevContent?.title || '');
    setDescription(prevContent?.description || '');
    setImageUrl(prevContent?.image_url || '');
    setMeditationType(prevContent?.content_type || defaultMeditationType || '');
    setPublished(prevContent?.published || false);

    if (defaultMeditationType && !(defaultMeditationType in CONTENT_TYPES)) {
      console.error(
        `Unexpected defaultMeditationType: '${defaultMeditationType}'`,
      );
    }
  }, [prevContent, defaultMeditationType]);

  useEffect(() => {
    setPrevConfigs(contentWithConfig?.configs || []);
    setNewConfigs(structuredClone(contentWithConfig?.configs || []));
    setViewingConfigIdx(contentWithConfig?.configs?.length ? 0 : -1);
  }, [contentWithConfig?.configs]);

  // edit a given meditation
  const handleEdit = async () => {
    setIsSaving(true);
    try {
      const { data, error } = await supabase
        .from('content')
        .update({
          title,
          description,
          image_url: imageUrl,
          content_type: meditationType,
          published,
        })
        .eq('id', prevContent.id);

      if (error) throw error;

      toast({
        title: 'Meditatie bijgewerkt',
        description: 'De meditatie is succesvol bijgewerkt.',
      });
    } catch (error) {
      toast({
        title: 'Fout bij opslaan',
        description: error.message,
      });
      return;
    } finally {
      setIsSaving(false);
    }

    setIsSaving(true);
    const configsTodelete = prevConfigs.filter(
      (c) => !newConfigs.some((nc) => nc.id === c.id),
    );
    const configsToCreate = newConfigs.filter((c) => !c.id);
    const configsToUpdate = newConfigs.filter((c) => c.id);

    console.log(
      `deleting ${configsTodelete.length} configs, creating ${configsToCreate.length} configs, updating ${configsToUpdate.length} configs`,
    );

    // delete any missing configs in a single transaction
    try {
      if (configsTodelete.length > 0) {
        const { error } = await supabase
          .from('content_configs')
          .delete()
          .in(
            'id',
            configsTodelete.map((c) => c.id),
          );
        if (error) throw error;
        toast({
          title: `${configsTodelete.length} configuraties verwijderd`,
          description: 'De configuraties zijn succesvol verwijderd.',
        });
      }
    } catch (error) {
      toast({
        title: 'Fout bij verwijderen configuraties',
        description: error.message,
      });
    } finally {
      setIsSaving(false);
    }

    // create any new configs
    const configPromises = configsToCreate.map((config) => {
      const payload: ContentConfigInsert = {
        content_id: prevContent.id,
        audio_link: config.audio_link,
        music: config.music,
        voice: config.voice,
        voice_name: config.voice_name,
        transcription_url: config.transcription_url,
      };
      return supabase.from('content_configs').insert(payload);
    });

    // update any existing configs
    const updatePromises = configsToUpdate.map((config) => {
      const payload: ContentConfigEditOrInsert = {
        id: config.id,
        content_id: prevContent.id,
        audio_link: config.audio_link,
        music: config.music,
        voice: config.voice,
        voice_name: config.voice_name,
        transcription_url: config.transcription_url,
      };
      return supabase
        .from('content_configs')
        .update(payload)
        .eq('id', config.id);
    });

    const allResults = await Promise.all([
      ...configPromises,
      ...updatePromises,
    ]);
    const errors = allResults.filter((r) => r.error).map((r) => r.error);
    if (errors.length > 0) {
      toast({
        title: `${errors.length} configuraties niet opgeslagen`,
        description: errors.map((e) => e.message).join(', '),
      });
      return;
    }

    onSave();
    handleClose();
  };

  // create a new meditation
  const handleCreate = async () => {
    let createdData;
    setIsSaving(true);
    try {
      const { data, error } = await supabase
        .from('content')
        .insert({
          title,
          description,
          image_url: imageUrl,
          content_type: meditationType,
          published,
        })
        .select();
      if (error) throw error;
      createdData = data;
      toast({
        title: 'Meditatie toegevoegd',
        description: 'De meditatie is succesvol toegevoegd.',
      });
      onSave();
      handleClose();
    } catch (error) {
      toast({
        title: 'fout bij het creëren van meditatie',
        description: error.message,
      });
      return;
    } finally {
      setIsSaving(false);
    }

    try {
      // Create all configs in parallel
      const configPromises = newConfigs.map((config) => {
        const payload: ContentConfigInsert = {
          content_id: createdData[0].id,
          audio_link: config.audio_link,
          music: config.music,
          voice: config.voice,
          voice_name: config.voice_name,
          transcription_url: config.transcription_url,
        };
        return supabase.from('content_configs').insert(payload);
      });

      const results = await Promise.all(configPromises);

      // Check for any errors
      const errors = results
        .filter((r) => r.error || r.status !== 201)
        .map((r) => r.error);
      if (errors.length > 0) {
        throw new Error(
          `Error creating ${errors.length}/${configPromises.length} configs: ${errors.map((e) => e.message).join(', ')}`,
        );
      }
    } catch (error) {
      toast({
        title: 'Fout bij opslaan configuraties',
        description: error.message,
      });
    }
  };

  const handleDelete = async () => {
    if (!prevContent) return;

    setIsSaving(true);
    try {
      // delete the content itself
      // NOTE: content configs should be deleted automatically via cascade
      const { error: contentError } = await supabase
        .from('content')
        .delete()
        .eq('id', prevContent.id);

      if (contentError) throw contentError;

      toast({
        title: 'Meditatie verwijderd',
        description: 'De meditatie is succesvol verwijderd.',
      });

      onSave();
      handleClose();
    } catch (error) {
      toast({
        title: 'Fout bij verwijderen',
        description: error.message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const pageTitle = prevContent ? 'Meditatie Bewerken' : 'Meditatie Toevoegen';
  const editMode = !!prevContent;

  // const allValidConfigs = newConfigs.reduce(c )
  const allValidConfigs = newConfigs.reduce(
    (allValid, config) => allValid && isCompleteConfig(config),
    true,
  );

  // allValidConfigs = allValidConfigs && newConfigs.length > 0; // require 1 or more configs

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => (open ? null : handleClose())}
    >
      <DialogContent
        className="sm:max-w-[1000px]"
        aria-label="Meditatie bewerken"
      >
        <DialogHeader>
          <DialogTitle>{pageTitle}</DialogTitle>
          <DialogDescription className="sr-only">{pageTitle}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-2">
          <div>
            <Label htmlFor="title">Titel</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="description">Beschrijving</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Meditatie Type</Label>
              <Select
                value={meditationType}
                onValueChange={(value) => setMeditationType(value)}
                required={true}
                disabled={Boolean(defaultMeditationType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(CONTENT_TYPES).map((content_name, idx) => (
                    <SelectItem value={content_name} key={content_name}>
                      {CONTENT_TYPES[content_name]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <ContentImageField
            value={imageUrl}
            onChange={setImageUrl}
            label="Afbeelding"
            required
          />

          <div className="space-y-1">
            {prevContent && (
              <ContentKeywordManager contentId={prevContent.id} />
            )}
          </div>

          {/* content configs */}
          <div
            className="pt-4 shadow-lg"
            style={{
              border: '1px solid black',
              borderRadius: '8px',
              padding: '8px',
            }}
          >
            <h3>Audio Configuraties ({newConfigs.length})</h3>
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs px-3"
              style={{ margin: '8px 0px 8px 0px', opacity: '0.5' }}
              onClick={() => {
                const isMusic = newConfigs.length === 0;
                setNewConfigs([
                  ...newConfigs,
                  {
                    ...structuredClone(EMPTY_CONFIG),
                    content_id: prevContent?.id || '',
                    music: isMusic,
                    voice: !isMusic,
                  },
                ]);
                setViewingConfigIdx((prev) => Math.max(prev, 0));
              }}
            >
              Configuratie Toevoegen
            </Button>

            <TabsRoot
              value={String(viewingConfigIdx)}
              onValueChange={(newTab) => setViewingConfigIdx(Number(newTab))}
            >
              <TabsList className="flex border-b">
                {newConfigs.map((config, idx) => {
                  return (
                    <TabsTrigger
                      value={String(idx)}
                      key={idx}
                      className="px-4 py-2 -mb-px text-sm font-medium text-gray-600 border-b-2 border-transparent hover:text-gray-800 hover:border-gray-300 focus:outline-none focus:text-gray-800 focus:border-gray-300 data-[state=active]:text-green-600 data-[state=active]:border-green-500"
                    >
                      {nameConfig(config)}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* render a tab for each configuration  */}
              {newConfigs.map((config, idx) => (
                <TabsContent value={String(idx)} key={idx} className="pt-4">
                  <div className="flex flex-col space-y-4">
                    <ConfigEditView
                      musicAllowed={
                        newConfigs.filter((c) => c.music).length === 0
                      }
                      key={idx}
                      config={config}
                      onUpdate={(updatedConfig) => {
                        setNewConfigs((prev) => {
                          const tmp = structuredClone(prev);
                          tmp[idx] = updatedConfig;
                          return tmp;
                        });
                      }}
                      onDelete={() => {
                        setNewConfigs((prev) =>
                          prev.filter((_, i) => i !== idx),
                        );
                        setViewingConfigIdx((prevIdx) => {
                          return Math.max(newConfigs.length - 2, prevIdx - 1);
                        });
                      }}
                    />
                  </div>
                </TabsContent>
              ))}
            </TabsRoot>
          </div>
        </div>

        <div className="space-y-1">
          <Switch
            id="published"
            checked={published}
            onCheckedChange={setPublished}
            className={`${published ? 'bg-green-500' : ''} data-[state=checked]:bg-green-500`}
          />
          <Label htmlFor="published" style={{ marginLeft: '10px' }}>
            Gepubliceerd
          </Label>
        </div>

        <DialogFooter>
          {editMode && (
            <AlertDialog
              open={showDeleteDialog}
              onOpenChange={setShowDeleteDialog}
            >
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  disabled={isSaving}
                  className="mr-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Verwijderen Meditatie
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Meditatie verwijderen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Weet u zeker dat u nu deze meditatie wilt verwijderen? Deze
                    actie kan niet ongedaan worden gemaakt.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuleren</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>
                    Verwijderen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button variant="outline" onClick={() => handleClose()}>
            Annuleren
          </Button>
          <Button
            onClick={editMode ? handleEdit : handleCreate}
            disabled={isSaving || !allValidConfigs}
          >
            {isSaving ? 'Opslaan...' : `${editMode ? 'Wijzigen ' : ''}opslaan`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
